import React from 'react'
import Grid from '@material-ui/core/Grid'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import CloseIcon from '@material-ui/icons/Close'
import { Loading, ConfirmationModal } from 'src/components'
import { useStyles } from './styles'
import { useGalleryModal } from './hooks/useGalleryModal'
import { UploadingCard } from './components/UploadingCard'
import { AnimalGalleryProps } from './types'

export const AnimalGallery: React.FC<AnimalGalleryProps> = ({
  photos,
  onAddPhotos,
  onRemovePhoto,
  onRetryUpload,
  isLoading,
  uploadingPhotos
}) => {
  const styles = useStyles()
  const {
    selectedPhoto,
    confirmDeletePhoto,
    fileInputRef,
    openPreview,
    closePreview,
    openDeleteConfirm,
    closeDeleteConfirm,
    triggerFileInput
  } = useGalleryModal()

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList || fileList.length === 0) return

    const imageFiles = Array.from(fileList).filter((file) => file.type.startsWith('image/'))
    if (imageFiles.length > 0) {
      onAddPhotos(imageFiles)
    }
    e.target.value = ''
  }

  const handleDeleteClick = (e: React.MouseEvent, photo: (typeof photos)[0]) => {
    e.stopPropagation()
    openDeleteConfirm(photo)
  }

  const handlePreviewDelete = () => {
    if (selectedPhoto) {
      openDeleteConfirm(selectedPhoto)
    }
  }

  const handleConfirmDelete = () => {
    if (confirmDeletePhoto) {
      onRemovePhoto(confirmDeletePhoto.id)
      closeDeleteConfirm()
      closePreview()
    }
  }

  const handleCancelDelete = () => {
    closeDeleteConfirm()
  }

  const hasUploading = uploadingPhotos.length > 0

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <Loading />
      </div>
    )
  }

  return (
    <>
      {photos.length === 0 && !hasUploading ? (
        <div className={styles.emptyState}>
          <button className={styles.emptyAddButton} onClick={triggerFileInput}>
            <AddIcon className={styles.addPhotoIcon} />
            <span className={styles.addPhotoText}>Adicionar fotos</span>
          </button>
          <p className={styles.emptyText}>Nenhuma foto adicionada ainda</p>
        </div>
      ) : (
        <Grid container spacing={2}>
          {photos.map((photo) => (
            <Grid item xs={6} sm={4} md={3} key={photo.id}>
              <div className={styles.photoCard} onClick={() => openPreview(photo)}>
                <img src={photo.url} alt={photo.description || 'Foto do animal'} className={styles.photoImage} />
                <div className={styles.photoOverlay}>
                  <button className={styles.deleteButton} onClick={(e) => handleDeleteClick(e, photo)}>
                    <DeleteIcon />
                  </button>
                </div>
              </div>
            </Grid>
          ))}

          {uploadingPhotos.map((photo) => (
            <Grid item xs={6} sm={4} md={3} key={photo.id}>
              <UploadingCard photo={photo} onRetry={onRetryUpload} />
            </Grid>
          ))}

          <Grid item xs={6} sm={4} md={3}>
            <div className={styles.addPhotoCard} onClick={triggerFileInput}>
              <div className={styles.addPhotoContent}>
                <AddIcon className={styles.addPhotoIcon} />
                <span className={styles.addPhotoText}>Adicionar fotos</span>
              </div>
            </div>
          </Grid>
        </Grid>
      )}

      <input
        ref={fileInputRef}
        type='file'
        accept='image/*'
        multiple
        onChange={handleFileChange}
        className={styles.hiddenInput}
      />

      {selectedPhoto && (
        <div className={styles.previewBackdrop} onClick={closePreview}>
          <div className={styles.previewContainer} onClick={(e) => e.stopPropagation()}>
            <div className={styles.previewActions}>
              <button className={styles.previewActionButton} onClick={handlePreviewDelete}>
                <DeleteIcon />
              </button>
              <button className={styles.previewActionButton} onClick={closePreview}>
                <CloseIcon />
              </button>
            </div>
            <img
              src={selectedPhoto.url}
              alt={selectedPhoto.description || 'Foto do animal'}
              className={styles.previewImage}
            />
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={!!confirmDeletePhoto}
        title='Remover foto'
        message='Tem certeza que deseja remover esta foto?'
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText='Remover'
        cancelText='Cancelar'
      />
    </>
  )
}
